import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { CompanyLicensingTabView } from '../view/CompanyLicensingTabView';
import { CompanyLicensingTabRepository } from '../repository/CompanyLicensingTabRepository';
import { CompanyLicensingTabWebComponent } from '../component/CompanyLicensingTabWebComponent';
import { templateCompanyLicensingTab } from '../view/template';
import { LicensingViewData } from '../types/licensing-view-data';
import { CHANGE_PARTNER_EVENT, CLOSE_PARTNER_MENU_EVENT } from '../../../myCompany/changePartnerMenu/types/types';
import { MonthlyUsageReportParams, NavigationHelper } from '@nirvana/shared/navigation';
import { ADD_PRODUCT_EVENT, CLOSE_ADD_PRODUCT_MENU_EVENT } from '../../addProductMenu/types/types';
import { GridEvents } from '../../../../grid/types/common';
import { LicenseUsage } from '../../licensingGrid/types/licensing-grid-license';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { RemoveProductConfirmationWindow } from '../../removeProductConfirmationWindow/RemoveProductConfirmationWindow';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { CLOSE_EDIT_LICENSING_OPTIONS_MENU_EVENT } from '../../editLicensingOptionsMenu/EditLicensingOptionsMenu.Constants';
import { LicensingTabEvents } from '../LicensingEnums';
import { EditCompanyPanelEvents } from '../../../editCompany/editCompanyPanel/view/EditCompanyPanelEnums';
import { SideMenuEvents } from '../../../../ui/gzSideMenu/types';
import { CompanyData } from '../../../types/company-data';
import { OnCompanyPanelClose } from '../../events/CompanyPanelEvent';
import { LicenseRenderers } from '../../licenseRenderers/LicenseRenderers';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';

export class CompanyLicensingTabController extends BaseWebComponentController {
  public component: CompanyLicensingTabWebComponent;
  public view: CompanyLicensingTabView;
  public viewConstructor: typeof CompanyLicensingTabView;
  public dataRepository: CompanyLicensingTabRepository;
  public dataRepositoryConstructor: typeof CompanyLicensingTabRepository;

  private selectedItemsGridData: LicenseUsage[];

  constructor(component: CompanyLicensingTabWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyLicensingTabView,
      viewTemplate: templateCompanyLicensingTab,
      dataRepositoryConstructor: CompanyLicensingTabRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.partnerCard.$showChangePartnerMenuButton,
        event: 'click',
        callback: this.openChangePartnerSideMenu,
      },
      {
        element: this.view.dom.licenseCard.licensingCardButtons.$showMonthlyUsageReportButton,
        event: 'click',
        callback: this.openReportWindow,
      },
      {
        element: this.view.dom.licenseCard.licensingCardButtons.$changeToPrepaidLicenseButton,
        event: 'click',
        callback: this.openChangeMspToYearlyLicensing,
      },
      {
        element: this.view.dom.licenseCard.licensingCardButtons.$editLicensingOptionsButton,
        event: 'click',
        callback: this.openEditLicensingOptions,
      },
      {
        element: this.view.dom.licenseUsageDetails.$addProductButton,
        event: 'click',
        callback: this.openAddProductSideMenu,
      },
      {
        element: this.view.dom.licenseUsageDetails.$deleteProductButton,
        event: 'click',
        callback: this.showRemoveProductConfirmationWindow.bind(this),
      },
      {
        element: this.view.dom.licenseUsageDetails.$buyProductButton,
        event: 'click',
        callback: this.handleBuyProductClick.bind(this),
      },
      {
        element: this.view.dom.licenseUsageDetails.$refreshProductButton,
        event: 'click',
        callback: this.refreshLicenseProductsInformation.bind(this),
      },
      {
        element: this.view.sideMenuParent,
        event: CHANGE_PARTNER_EVENT,
        callback: this.handleChangePartnerEvent,
      },
      {
        element: this.view.sideMenuParent,
        event: CLOSE_PARTNER_MENU_EVENT,
        callback: this.closeChangePartnerSideMenu,
      },
      {
        element: this.view.sideMenuParent,
        event: ADD_PRODUCT_EVENT,
        callback: this.addProductEventHandler,
      },
      {
        element: this.view.sideMenuParent,
        event: CLOSE_ADD_PRODUCT_MENU_EVENT,
        callback: this.closeAddProductSideMenu,
      },
      {
        element: this.view.sideMenuParent,
        event: CLOSE_EDIT_LICENSING_OPTIONS_MENU_EVENT, // fired when Cancel/Save buttons are pressed
        callback: this.onEditLicensingOptionsMenuClosed.bind(this, true),
      },
      {
        element: this.view.sideMenuParent,
        event: SideMenuEvents.CLOSE, // fired when 'x' button is pressed
        callback: this.onEditLicensingOptionsMenuClosed.bind(this, false),
      },
      {
        element: this.view.dom.licenseUsageDetails.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.handleLicenseGridRowsSelectedEvent,
      },
      {
        element: this.view.dom.licenseUsageDetails.logoutNotice.$logoutButton,
        event: 'click',
        callback: this.logoutEventHandler.bind(this),
      },
      {
        element: this.view.sideMenuParent,
        event: LicensingTabEvents.LicenseOptionsChange,
        callback: this.changeLicensingOptionsEventHandler,
      },
    ];
  }

  public async getDataForView(): Promise<LicensingViewData> {
    const company = this.component.initialCompanyData;
    const isEdit = this.component.context === CompanyActionContext.Edit;

    return {
      loggedUser: await this.dataRepository.getLoggedUser(),
      company: company,
      context: this.component.context,
      companyLicenseInfo: isEdit ? await this.dataRepository.getCompanyLicenseInformation(company._id) : null,
    };
  }

  public async openAddProductSideMenu() {
    this.view.$addProductSideMenu.openSideMenu();
    await this.view.$addProduct.reloadMenu();
  }

  public async openChangeMspToYearlyLicensing() {
    this.view.$changeMspToYearlyLicensingSideMenu.openSideMenu();
    await this.view.$changeMspToYearlyLicensing.reloadMenu();
  }

  public async openEditLicensingOptions() {
    this.view.$editLicensingOptionsSideMenu.openSideMenu();
  }

  private async openChangePartnerSideMenu() {
    this.view.$changePartnerSideMenu.openSideMenu();
    await this.view.$changePartner.reloadMenu();
  }

  private async openReportWindow() {
    this.view.setLoadingState();

    const companyData = await this.getCompanyData();
    const params: MonthlyUsageReportParams = {
      title: this.dependencies.translateFn('Monthly License Usage Report'),
      targets: [
        {
          protectedEntityId: companyData._id,
          hash: companyData.specifics.hash,
          parentCompanyId: companyData.company?.id,
          name: companyData.name,
          type: companyData.type,
          path: companyData.path,
        },
      ],
    };

    NavigationHelper.navigateToMonthlyUsageReport(this.dependencies.Ext, params);

    this.view.removeLoadingState();
    this.component.dispatchEvent(new OnCompanyPanelClose());
  }

  private closeChangePartnerSideMenu() {
    this.view.$changePartnerSideMenu.closeSideMenu();
  }

  private closeAddProductSideMenu() {
    if (this.view.$addProductSideMenu) {
      this.view.$addProductSideMenu.closeSideMenu();
    }
    if (this.view.$changeMspToYearlyLicensingSideMenu) {
      this.view.$changeMspToYearlyLicensingSideMenu.closeSideMenu();
    }
  }

  private onEditLicensingOptionsMenuClosed(shouldCloseSideMenu: boolean) {
    // reset edit licensing options to initial values
    this.view.updateEditLicensingOptions();

    if (shouldCloseSideMenu) {
      this.view.$editLicensingOptionsSideMenu?.closeSideMenu(false);
    }
  }

  private async refreshCards(): Promise<void> {
    try {
      const company = await this.getCompanyData();
      const loggedUser = await this.dataRepository.getLoggedUser();

      await this.view.updateParentCompanyInformation(company, loggedUser);
    } catch (error) {
      this.showErrorToast(error);
    }
  }

  private async addProductEventHandler(event: WebComponentEvent) {
    if (event.detail.shouldClosePanel) {
      this.component.dispatchEvent(new OnCompanyPanelClose());
      return;
    }

    await this.refreshCompanyLicenseDetails();

    const shouldLogoutTextBeVisible = this.shouldLogoutTextBeVisibleForLicense(
      event.detail.license.isAddon ?? false,
      event.detail.license.productId
    );
    this.view.changeLogoutNoticeVisibility(shouldLogoutTextBeVisible);
  }

  private async changeLicensingOptionsEventHandler(event: WebComponentEvent) {
    await this.refreshCompanyLicenseDetails();

    this.view.changeLogoutNoticeVisibility(event.detail.shouldWarningBeTriggered);
  }

  private async refreshCompanyLicenseDetails(): Promise<void> {
    try {
      const company = await this.getCompanyData();
      const companyLicenseInfo =
        this.component.context === CompanyActionContext.Edit
          ? await this.dataRepository.getCompanyLicenseInformation(company._id)
          : null;

      await this.view.updateCompanyInformation(company, companyLicenseInfo);

      if (this.component.context === CompanyActionContext.MyCompany) {
        const loggedUser = await this.dataRepository.getLoggedUser();
        await this.view.updateParentCompanyInformation(company, loggedUser);
      }

      this.handleLicensingTabInfoRefresh(company);
    } catch (error) {
      this.showErrorToast(error);
    }
  }

  private handleLicenseGridRowsSelectedEvent(event: WebComponentEvent) {
    this.selectedItemsGridData = event.detail.selectedItemsData;

    this.view.dom.licenseUsageDetails.$deleteProductButton.disabled = !this.shouldDeleteButtonBeAvailable();
    this.view.dom.licenseUsageDetails.$buyProductButton.disabled = !this.shouldBuyButtonBeAvailable();
  }

  private shouldDeleteButtonBeAvailable(): boolean {
    const nonRemovableAddons = [
      LicenseProductIdEnum.ProductIdBreachWarranty,
      LicenseProductIdEnum.ProductIdBreachWarrantyPlus,
      LicenseProductIdEnum.ProductIdBreachWarrantyPlusOver1000,
    ];

    const nonRemovableAddonDependencies = [LicenseProductIdEnum.ProductIdMdr, LicenseProductIdEnum.ProductIdMdrPlus];

    // edited company is readonly -> delete button should be disabled
    if (this.view.isCompanyReadOnly(this.view.company)) {
      return false;
    }

    // no selected items -> delete button should be disabled
    if (!(this.selectedItemsGridData && this.selectedItemsGridData.length > 0)) {
      return false;
    }

    // non-removable addons

    const isNonRemovableAddonSelected = this.selectedItemsGridData.some(selectedItemData =>
      nonRemovableAddons.includes(selectedItemData.productId)
    );

    const isNonRemovableAddonDependencySelected = this.selectedItemsGridData.some(selectedItemData =>
      nonRemovableAddonDependencies.includes(selectedItemData.productId)
    );

    if (isNonRemovableAddonSelected && !isNonRemovableAddonDependencySelected) {
      return false;
    }

    // partner case
    if (this.view.company.specifics.rights.manageCompanies) {
      return true;
    }

    // customer case
    for (const licenseUsageData of this.view.usageDetails.data) {
      const isSelected =
        this.selectedItemsGridData.filter(
          selectedItemData => selectedItemData.licenseKey === licenseUsageData.licenseKey
        ).length > 0;

      if (!licenseUsageData.isAddon && !isSelected) {
        return true;
      }
    }

    return false;
  }

  private shouldBuyButtonBeAvailable(): boolean {
    // edited company is readonly -> delete button should be disabled
    return !this.view.isCompanyReadOnly(this.view.company);
  }

  private showRemoveProductConfirmationWindow() {
    const confirmCallback = async () => {
      this.dispatchLoadingEvent(true);
      const shouldLogoutTextBeVisible = this.shouldLogoutTextBeVisibleForSelectedItems();
      const removedProduct = await this.removeLicenseProducts();
      await this.refreshCompanyLicenseDetails();
      this.dispatchLoadingEvent(false);
      this.view.changeLogoutNoticeVisibility(removedProduct && shouldLogoutTextBeVisible);
    };

    RemoveProductConfirmationWindow.showRemoveProductConfirmationWindow(
      this.dependencies.translateFn,
      null,
      confirmCallback
    );
  }

  private async removeLicenseProducts(): Promise<boolean> {
    this.view.dom.licenseUsageDetails.$deleteProductButton.disabled = true;
    this.view.dom.licenseUsageDetails.$deleteProductButton.showSpinner = true;

    const selectedKeyItems = this.selectedItemsGridData.map(selectedItemGridData => selectedItemGridData.licenseKey);

    try {
      await this.dependencies.api.clients.Company.RemoveLicenseProducts(
        this.view.company._id,
        selectedKeyItems
      ).toPromise();

      this.showSuccessToast(this.dependencies.translateFn('The product was removed successfully.'));
      return true;
    } catch (error) {
      this.showErrorToast(error);
      return false;
    } finally {
      this.view.dom.licenseUsageDetails.$deleteProductButton.disabled = false;
      this.view.dom.licenseUsageDetails.$deleteProductButton.showSpinner = false;
    }
  }

  private async refreshLicenseProductsInformation() {
    this.view.dom.licenseUsageDetails.$refreshProductButton.showSpinner = true;

    try {
      this.dispatchLoadingEvent(true);

      this.view.dom.licenseUsageDetails.$refreshProductButton.disabled = true;
      await this.dependencies.api.clients.Licenses.RefreshLicenseProductsInformation(this.view.company._id).toPromise();

      await this.refreshCompanyLicenseDetails();
    } catch (error) {
      this.showErrorToast(error);
      this.view.dom.licenseUsageDetails.$refreshProductButton.disabled = false;
    } finally {
      this.dispatchLoadingEvent(false);
    }

    this.view.dom.licenseUsageDetails.$refreshProductButton.showSpinner = false;
  }

  private shouldLogoutTextBeVisibleForSelectedItems() {
    return (
      this.selectedItemsGridData.filter(selectedItemData =>
        this.shouldLogoutTextBeVisibleForLicense(!!selectedItemData.isAddon, selectedItemData.productId)
      ).length > 0
    );
  }

  private shouldLogoutTextBeVisibleForLicense(isAddon: boolean, productId?: LicenseProductIdEnum): boolean {
    const isAddonWithoutUsage = LicenseRenderers.isAddonWithoutUsage(isAddon, productId);
    const isEmailSecurity = productId === LicenseProductIdEnum.ProductIdEmailSecurity;

    // logout notice should be hidden for addons without usage and email security
    return !isAddonWithoutUsage && !isEmailSecurity;
  }

  private async logoutEventHandler() {
    this.view.dom.licenseUsageDetails.logoutNotice.$logoutButton.disabled = true;
    this.view.dom.licenseUsageDetails.logoutNotice.$logoutButton.showSpinner = true;

    try {
      await this.clearSessionForUsersInCompany();

      if (this.component.context === CompanyActionContext.Edit) {
        this.showSuccessToast(this.dependencies.translateFn('You have successfully logged out all users.'));
        this.view.changeLogoutNoticeVisibility(false, true);
      }
    } catch (error) {
      this.showErrorToast(error);
    } finally {
      this.view.dom.licenseUsageDetails.logoutNotice.$logoutButton.disabled = false;
      this.view.dom.licenseUsageDetails.logoutNotice.$logoutButton.showSpinner = false;
    }
  }

  private showSuccessToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(error: Error) {
    let errorMessage;

    if (error['exceptionType'] === 'Exceptions\\NoInternetConnection') {
      errorMessage = this.dependencies.translateFn(
        'The licensing server is temporarily unavailable. Please try again later.'
      );
    } else {
      errorMessage = error?.message || this.translator.getGenericErrorMessage();
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private async getCompanyData() {
    return this.component.context === CompanyActionContext.MyCompany
      ? await this.dataRepository.getMyCompanyData()
      : await this.dataRepository.getManagedCompanyById(this.view.company._id);
  }

  private async clearSessionForUsersInCompany() {
    if (this.component.context === CompanyActionContext.MyCompany) {
      await this.dependencies.api.clients.Users.ClearSessionsForUsersInCurrentCompany().toPromise();
    } else {
      await this.dependencies.api.clients.Users.ClearSessionsForUsersInEditedCompany(this.view.company._id).toPromise();
    }
  }

  private handleLicensingTabInfoRefresh(company: CompanyData) {
    this.component.dispatchEvent(
      new CustomEvent(LicensingTabEvents.LicensingTabRefresh, {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          updatedCompany: company,
        },
      })
    );
  }

  private handleBuyProductClick() {
    const selectedGridItems = this.selectedItemsGridData;

    let productId: number | undefined;
    if (selectedGridItems.length === 1) {
      productId = selectedGridItems[0].productId;
    }

    this.component.dispatchEvent(
      new CustomEvent(LicensingTabEvents.LicensingGridProductBuyClick, {
        bubbles: false,
        cancelable: true,
        composed: false,
        detail: {
          productId: productId,
        },
      })
    );
  }

  private dispatchLoadingEvent(operationInProgress: boolean) {
    if (this.component.context !== CompanyActionContext.Edit) {
      return;
    }

    // Dispatch event only when editing a company
    this.component.dispatchEvent(
      new CustomEvent(EditCompanyPanelEvents.LicensingTabOperationInProgress, {
        detail: { isOperationInProgress: operationInProgress },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async handleChangePartnerEvent(): Promise<void> {
    if (this.component.context !== CompanyActionContext.MyCompany) {
      return;
    }

    await this.refreshCompanyLicenseDetails();
  }
}
